'use client';
import React, { useState } from 'react';
import {
  AlertTriangle, Shield, Flag, Users, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, ArrowRight, Download, RefreshCw,
  Search, MessageSquare, FileText, Eye, AlertCircle, Zap
} from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';


type SeverityBand = 'Critical' | 'High' | 'Medium' | 'Low';
type HitType = 'Adverse Media' | 'PEP Match' | 'Sanctions' | 'Watchlist';
type Disposition = 'Pending Review' | 'Escalated to MLRO' | 'False Positive' | 'Confirmed Match' | 'Cleared' | 'Blocked';

const severityBg = (s: SeverityBand) => {
  if (s === 'Critical') return 'bg-critical/10 text-critical border-critical/30';
  if (s === 'High') return 'bg-warning/10 text-warning border-warning/30';
  if (s === 'Medium') return 'bg-amber-400/10 text-amber-400 border-amber-400/30';
  return 'bg-positive/10 text-positive border-positive/30';
};

const severityDot = (s: SeverityBand) => {
  if (s === 'Critical') return 'bg-critical';
  if (s === 'High') return 'bg-warning';
  if (s === 'Medium') return 'bg-amber-400';
  return 'bg-positive';
};

const hitTypeIcon = (t: HitType) => {
  if (t === 'Adverse Media') return AlertTriangle;
  if (t === 'PEP Match') return Users;
  if (t === 'Sanctions') return Shield;
  return Flag;
};

const hitTypeBg = (t: HitType) => {
  if (t === 'Adverse Media') return 'bg-warning/10 text-warning';
  if (t === 'PEP Match') return 'bg-amber-400/10 text-amber-400';
  if (t === 'Sanctions') return 'bg-critical/10 text-critical';
  return 'bg-info/10 text-info';
};

const dispositionBg = (d: Disposition) => {
  if (d === 'Confirmed Match' || d === 'Blocked') return 'bg-critical/10 text-critical border-critical/30';
  if (d === 'Escalated to MLRO') return 'bg-warning/10 text-warning border-warning/30';
  if (d === 'Pending Review') return 'bg-info/10 text-info border-info/30';
  if (d === 'False Positive' || d === 'Cleared') return 'bg-positive/10 text-positive border-positive/30';
  return 'bg-surface-3 text-foreground-muted border-border';
};

const screeningHits = [
  {
    id: 'SCR-2026-0041',
    entity: 'Thornfield Capital Holdings Ltd',
    entityId: 'INV-001',
    entityType: 'Investor',
    hitType: 'PEP Match' as HitType,
    severity: 'High' as SeverityBand,
    disposition: 'Escalated to MLRO' as Disposition,
    matchScore: 87,
    source: 'Dow Jones Risk & Compliance',
    matchedName: 'Thornfield Capital Holdings Limited',
    description: 'Director of record (James Thornfield) identified as Politically Exposed Person — former UK Treasury official. Indirect PEP exposure via beneficial ownership chain.',
    detectedAt: '2026-08-20 09:14',
    assignee: 'L. Okafor',
    mlroRef: 'MLRO-2026-0089',
    mlroEscalatedAt: '2026-08-20 10:30',
    mlroDecision: null,
    notes: 'Awaiting MLRO review. Enhanced due diligence documentation requested from entity.',
    relatedTxns: ['TXN-884921', 'TXN-882003'],
    tags: ['PEP-Adjacent', 'Director-Level'],
  },
  {
    id: 'SCR-2026-0040',
    entity: 'Unknown Beneficial Owner Entity',
    entityId: 'INV-005',
    entityType: 'Investor',
    hitType: 'Sanctions' as HitType,
    severity: 'Critical' as SeverityBand,
    disposition: 'Blocked' as Disposition,
    matchScore: 94,
    source: 'OFAC SDN List',
    matchedName: 'Unknown Beneficial Owner Entity LLC',
    description: 'Entity name and registration details match OFAC SDN list entry. Beneficial ownership structure obscures ultimate controller. Investment blocked pending full investigation.',
    detectedAt: '2026-08-20 11:07',
    assignee: 'M. Patel',
    mlroRef: 'MLRO-2026-0090',
    mlroEscalatedAt: '2026-08-20 11:15',
    mlroDecision: 'Block and report',
    notes: 'MLRO confirmed block. SAR filing initiated. Funds frozen pending regulatory guidance.',
    relatedTxns: ['TXN-883104'],
    tags: ['OFAC-SDN', 'Blocked', 'SAR-Filed'],
  },
  {
    id: 'SCR-2026-0039',
    entity: 'Crestwood Family Office LLC',
    entityId: 'INV-002',
    entityType: 'Investor',
    hitType: 'Adverse Media' as HitType,
    severity: 'Medium' as SeverityBand,
    disposition: 'Pending Review' as Disposition,
    matchScore: 72,
    source: 'LexisNexis WorldCompliance',
    matchedName: 'Crestwood Family Office LLC',
    description: 'Adverse media articles (3) referencing civil litigation related to undisclosed offshore holdings. No criminal proceedings. Requires enhanced due diligence review.',
    detectedAt: '2026-08-19 14:22',
    assignee: 'S. Chen',
    mlroRef: null,
    mlroEscalatedAt: null,
    mlroDecision: null,
    notes: 'Analyst review in progress. Awaiting legal opinion on civil litigation materiality.',
    relatedTxns: ['TXN-881577'],
    tags: ['Civil-Litigation', 'Offshore-Holdings'],
  },
  {
    id: 'SCR-2026-0038',
    entity: 'Meridian Sovereign Fund',
    entityId: 'INV-004',
    entityType: 'Investor',
    hitType: 'PEP Match' as HitType,
    severity: 'Medium' as SeverityBand,
    disposition: 'False Positive' as Disposition,
    matchScore: 61,
    source: 'Refinitiv World-Check',
    matchedName: 'Meridian Sovereign Fund (Cayman)',
    description: 'Name similarity match to PEP-linked entity. Investigation confirmed different registration jurisdiction and distinct ownership structure. No actual PEP connection.',
    detectedAt: '2026-08-18 08:55',
    assignee: 'L. Okafor',
    mlroRef: null,
    mlroEscalatedAt: null,
    mlroDecision: null,
    notes: 'Confirmed false positive. Match score below threshold after manual review. Cleared for processing.',
    relatedTxns: [],
    tags: ['Name-Similarity', 'Cleared'],
  },
  {
    id: 'SCR-2026-0037',
    entity: 'Ashford Pension Trustees',
    entityId: 'INV-003',
    entityType: 'Investor',
    hitType: 'Watchlist' as HitType,
    severity: 'Low' as SeverityBand,
    disposition: 'Cleared' as Disposition,
    matchScore: 45,
    source: 'HM Treasury Consolidated List',
    matchedName: 'Ashford Pension Trust (unrelated)',
    description: 'Watchlist hit on similar entity name. Confirmed unrelated entity with different registration number and jurisdiction. Cleared after document verification.',
    detectedAt: '2026-08-17 11:30',
    assignee: 'S. Chen',
    mlroRef: null,
    mlroEscalatedAt: null,
    mlroDecision: null,
    notes: 'Cleared. Documents verified. No further action required.',
    relatedTxns: [],
    tags: ['Cleared'],
  },
];

const mlroWorkflowSteps = [
  { step: 1, label: 'Hit Detected', description: 'Automated screening system flags entity match', icon: Zap },
  { step: 2, label: 'Analyst Review', description: 'Compliance analyst assesses match quality and context', icon: Eye },
  { step: 3, label: 'MLRO Escalation', description: 'Analyst escalates confirmed/uncertain hits to MLRO', icon: ArrowRight },
  { step: 4, label: 'MLRO Decision', description: 'MLRO reviews evidence and issues disposition decision', icon: MessageSquare },
  { step: 5, label: 'Disposition Applied', description: 'Decision recorded, actions executed, audit trail updated', icon: CheckCircle },
];

type FilterSeverity = 'All' | SeverityBand;
type FilterType = 'All' | HitType;
type FilterDisposition = 'All' | Disposition;

export default function ScreeningHitsWorkflow() {
  const [expandedHit, setExpandedHit] = useState<string | null>('SCR-2026-0041');
  const [filterSeverity, setFilterSeverity] = useState<FilterSeverity>('All');
  const [filterType, setFilterType] = useState<FilterType>('All');
  const [filterDisposition, setFilterDisposition] = useState<FilterDisposition>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showWorkflow, setShowWorkflow] = useState(false);

  const filtered = screeningHits.filter(h => {
    if (filterSeverity !== 'All' && h.severity !== filterSeverity) return false;
    if (filterType !== 'All' && h.hitType !== filterType) return false;
    if (filterDisposition !== 'All' && h.disposition !== filterDisposition) return false;
    if (searchQuery && !h.entity.toLowerCase().includes(searchQuery.toLowerCase()) && !h.id.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const counts = {
    critical: screeningHits.filter(h => h.severity === 'Critical').length,
    high: screeningHits.filter(h => h.severity === 'High').length,
    pending: screeningHits.filter(h => h.disposition === 'Pending Review').length,
    escalated: screeningHits.filter(h => h.disposition === 'Escalated to MLRO').length,
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface-1">
        <div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">Compliance Screening Hits</h1>
          <p className="text-sm text-foreground-muted mt-0.5">Adverse media · PEP matches · Sanctions designations · MLRO escalation workflow · Disposition tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowWorkflow(!showWorkflow)}
            className="btn-ghost text-sm flex items-center gap-1.5"
          >
            <Eye size={14} /> {showWorkflow ? 'Hide' : 'Show'} Workflow
          </button>
          <button className="btn-ghost text-sm flex items-center gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn-secondary text-sm flex items-center gap-1.5">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-6 py-4 border-b border-border bg-surface-1">
        <MetricCard label="Critical Hits" value={String(counts.critical)} trend="neutral" trendValue="Require immediate action" variant="critical" />
        <MetricCard label="High Severity Hits" value={String(counts.high)} trend="neutral" trendValue="MLRO review required" variant="warning" />
        <MetricCard label="Pending Review" value={String(counts.pending)} trend="neutral" trendValue="Analyst queue" />
        <MetricCard label="Escalated to MLRO" value={String(counts.escalated)} trend="neutral" trendValue="Awaiting decision" />
      </div>

      {/* MLRO Workflow Banner */}
      {showWorkflow && (
        <div className="px-6 py-4 border-b border-border bg-surface-2/50">
          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">MLRO Escalation Workflow</p>
          <div className="flex items-start gap-0 overflow-x-auto">
            {mlroWorkflowSteps.map((step, idx) => (
              <div key={step.step} className="flex items-center flex-shrink-0">
                <div className="flex flex-col items-center text-center w-32">
                  <div className="w-9 h-9 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center mb-2">
                    <step.icon size={15} className="text-accent" />
                  </div>
                  <span className="text-xs font-semibold text-foreground">{step.label}</span>
                  <span className="text-xs text-foreground-muted mt-0.5 leading-tight">{step.description}</span>
                </div>
                {idx < mlroWorkflowSteps.length - 1 && (
                  <ArrowRight size={14} className="text-foreground-muted mx-1 flex-shrink-0 mt-[-20px]" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-border bg-surface-1">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            placeholder="Search entity or ref..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="input pl-8 py-1.5 text-sm w-full"
          />
        </div>
        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value as FilterSeverity)} className="input py-1.5 text-sm">
          <option value="All">All Severities</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value as FilterType)} className="input py-1.5 text-sm">
          <option value="All">All Hit Types</option>
          <option value="Adverse Media">Adverse Media</option>
          <option value="PEP Match">PEP Match</option>
          <option value="Sanctions">Sanctions</option>
          <option value="Watchlist">Watchlist</option>
        </select>
        <select value={filterDisposition} onChange={e => setFilterDisposition(e.target.value as FilterDisposition)} className="input py-1.5 text-sm">
          <option value="All">All Dispositions</option>
          <option value="Pending Review">Pending Review</option>
          <option value="Escalated to MLRO">Escalated to MLRO</option>
          <option value="Confirmed Match">Confirmed Match</option>
          <option value="False Positive">False Positive</option>
          <option value="Cleared">Cleared</option>
          <option value="Blocked">Blocked</option>
        </select>
        <span className="text-xs text-foreground-muted ml-auto">{filtered.length} of {screeningHits.length} hits</span>
      </div>

      {/* Hit List */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {filtered.map(hit => {
          const HitIcon = hitTypeIcon(hit.hitType);
          const isExpanded = expandedHit === hit.id;
          return (
            <div key={hit.id} className={`card overflow-hidden border ${hit.severity === 'Critical' ? 'border-critical/30' : hit.severity === 'High' ? 'border-warning/30' : 'border-border'}`}>
              {/* Hit Header */}
              <button
                className="w-full flex items-start gap-4 px-5 py-4 hover:bg-surface-2/40 transition-colors text-left"
                onClick={() => setExpandedHit(isExpanded ? null : hit.id)}
              >
                {/* Severity indicator */}
                <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${severityDot(hit.severity)}`} />

                {/* Hit type icon */}
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${hitTypeBg(hit.hitType)}`}>
                  <HitIcon size={16} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-foreground">{hit.entity}</span>
                    <span className="text-xs font-mono-data text-foreground-muted">{hit.id}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${severityBg(hit.severity)}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${severityDot(hit.severity)}`} />
                      {hit.severity}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${hitTypeBg(hit.hitType)}`}>{hit.hitType}</span>
                  </div>
                  <p className="text-xs text-foreground-muted line-clamp-1">{hit.description}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    <span className="text-xs text-foreground-muted">Source: <span className="text-foreground">{hit.source}</span></span>
                    <span className="text-xs text-foreground-muted">Match: <span className="font-mono-data font-bold text-foreground">{hit.matchScore}%</span></span>
                    <span className="text-xs text-foreground-muted">Detected: <span className="font-mono-data text-foreground">{hit.detectedAt}</span></span>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${dispositionBg(hit.disposition)}`}>{hit.disposition}</span>
                  {isExpanded ? <ChevronUp size={16} className="text-foreground-muted" /> : <ChevronDown size={16} className="text-foreground-muted" />}
                </div>
              </button>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-border bg-surface-2/20 px-5 py-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Match Details */}
                    <div>
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Match Details</p>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-foreground-muted block">Matched Name</span>
                          <span className="text-sm text-foreground font-medium">{hit.matchedName}</span>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-muted block">Match Confidence</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${hit.matchScore >= 80 ? 'bg-critical' : hit.matchScore >= 60 ? 'bg-warning' : 'bg-amber-400'}`}
                                style={{ width: `${hit.matchScore}%` }}
                              />
                            </div>
                            <span className="text-xs font-mono-data font-bold text-foreground">{hit.matchScore}%</span>
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-muted block">Data Source</span>
                          <span className="text-sm text-foreground">{hit.source}</span>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-muted block">Entity Type</span>
                          <span className="text-sm text-foreground">{hit.entityType} · {hit.entityId}</span>
                        </div>
                        {hit.relatedTxns.length > 0 && (
                          <div>
                            <span className="text-xs text-foreground-muted block mb-1">Related Transactions</span>
                            <div className="flex flex-wrap gap-1">
                              {hit.relatedTxns.map(txn => (
                                <span key={txn} className="px-2 py-0.5 rounded bg-surface-3 text-xs font-mono-data text-foreground">{txn}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* MLRO Escalation */}
                    <div>
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">MLRO Escalation</p>
                      {hit.mlroRef ? (
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs text-foreground-muted block">MLRO Reference</span>
                            <span className="text-sm font-mono-data text-accent">{hit.mlroRef}</span>
                          </div>
                          <div>
                            <span className="text-xs text-foreground-muted block">Escalated At</span>
                            <span className="text-sm font-mono-data text-foreground">{hit.mlroEscalatedAt}</span>
                          </div>
                          <div>
                            <span className="text-xs text-foreground-muted block">MLRO Decision</span>
                            {hit.mlroDecision ? (
                              <span className="text-sm font-medium text-foreground">{hit.mlroDecision}</span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-warning"><Clock size={12} /> Awaiting decision</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2 text-xs text-foreground-muted">
                            <AlertCircle size={13} />
                            {hit.disposition === 'False Positive' || hit.disposition === 'Cleared' ?'No escalation required — resolved at analyst level' :'Not yet escalated to MLRO'}
                          </div>
                          {hit.disposition === 'Pending Review' && (
                            <button className="btn-secondary text-xs flex items-center gap-1.5 w-fit mt-1">
                              <ArrowRight size={12} /> Escalate to MLRO
                            </button>
                          )}
                        </div>
                      )}

                      {/* Severity Band Indicator */}
                      <div className="mt-4 p-3 rounded-lg border border-border bg-surface-2/50">
                        <p className="text-xs font-semibold text-foreground-muted mb-2">Severity Band</p>
                        <div className="flex gap-1">
                          {(['Low', 'Medium', 'High', 'Critical'] as SeverityBand[]).map(band => (
                            <div
                              key={band}
                              className={`flex-1 h-2 rounded-full ${band === hit.severity ? severityDot(band) : 'bg-surface-3'}`}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-foreground-muted">Low</span>
                          <span className="text-xs font-semibold" style={{ color: hit.severity === 'Critical' ? 'var(--color-critical)' : hit.severity === 'High' ? 'var(--color-warning)' : '' }}>{hit.severity}</span>
                          <span className="text-xs text-foreground-muted">Critical</span>
                        </div>
                      </div>
                    </div>

                    {/* Disposition & Notes */}
                    <div>
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Disposition & Notes</p>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-foreground-muted block">Current Disposition</span>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border mt-0.5 ${dispositionBg(hit.disposition)}`}>
                            {hit.disposition === 'Cleared' || hit.disposition === 'False Positive' ? <CheckCircle size={11} /> :
                             hit.disposition === 'Blocked' ? <XCircle size={11} /> :
                             hit.disposition === 'Pending Review' ? <Clock size={11} /> :
                             <AlertTriangle size={11} />}
                            {hit.disposition}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-muted block">Assigned Analyst</span>
                          <span className="text-sm text-foreground">{hit.assignee}</span>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-muted block mb-1">Tags</span>
                          <div className="flex flex-wrap gap-1">
                            {hit.tags.map(tag => (
                              <span key={tag} className="px-2 py-0.5 rounded-full text-xs bg-surface-3 text-foreground-muted border border-border">{tag}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-muted block mb-1">Analyst Notes</span>
                          <p className="text-xs text-foreground bg-surface-2 rounded p-2 border border-border leading-relaxed">{hit.notes}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button className="btn-ghost text-xs flex items-center gap-1"><FileText size={12} /> View Full Report</button>
                        <button className="btn-ghost text-xs flex items-center gap-1"><Eye size={12} /> Entity Profile</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Shield size={40} className="text-foreground-muted mb-3 opacity-40" />
            <p className="text-foreground-muted text-sm">No screening hits match the current filters</p>
          </div>
        )}
      </div>
    </div>
  );
}
